import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import FormField from './FormField';
import { FORM_STATUS } from '../types';
import { getMissingRequiredFields, isAnswerableField } from '../formUtils';
import {
  loadPublicDynamicFormsForm,
  savePublicDynamicFormsDraft,
  submitPublicDynamicFormsForm,
} from '../api';

const formatDraftSavedTime = (date) => date.toLocaleTimeString([], {
  hour: 'numeric',
  minute: '2-digit',
});

const getMetadataFieldName = (fieldName, suffix) => `_df_meta_${suffix}_${fieldName}`;

const PublicFormPage = () => {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const latestDataRef = useRef({});
  const saveTimeoutRef = useRef(null);
  const submittedRef = useRef(false);
  const responseUidRef = useRef(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Smart PCMH Dynamic Forms';

    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);
    responseUidRef.current = null;

    loadPublicDynamicFormsForm(token)
      .then((loadedForm) => {
        if (cancelled) {
          return;
        }

        const data = loadedForm.data || {};
        const isReusable = Boolean(loadedForm.allowMultiplePublicSubmissions);
        const isCompleted = !isReusable && loadedForm.status === FORM_STATUS.COMPLETED;
        responseUidRef.current = loadedForm.responseUid || null;
        setForm(loadedForm);
        setFormData(data);
        latestDataRef.current = data;
        setLastSavedAt(null);
        submittedRef.current = isCompleted;
        setSubmitted(isCompleted);
        setLoading(false);
      })
      .catch((loadError) => {
        if (cancelled) {
          return;
        }

        console.error('Failed to load public dynamic form:', loadError);
        setError(loadError.status === 404 ? 'This form link is invalid, expired, or no longer available.' : 'Unable to load this form right now.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const saveDraft = useCallback((data, showSaving = false) => {
    if (submittedRef.current) {
      return Promise.resolve();
    }

    if (showSaving) {
      setSaving(true);
    }

    return savePublicDynamicFormsDraft(token, data, responseUidRef.current)
      .then((updatedForm) => {
        if (submittedRef.current) {
          return;
        }

        setForm(updatedForm);
        setLastSavedAt(new Date());
        if (showSaving) {
          setSaving(false);
        }
      })
      .catch((saveError) => {
        if (submittedRef.current) {
          if (showSaving) {
            setSaving(false);
          }
          return;
        }

        console.error('Failed to save public dynamic form draft:', saveError);
        setError('Unable to save your draft. Please check your connection and try again.');
        if (showSaving) {
          setSaving(false);
        }
      });
  }, [token]);

  useEffect(() => {
    if (!form || submitted) {
      return undefined;
    }

    const interval = setInterval(() => {
      saveDraft(latestDataRef.current, true);
    }, 30000);

    return () => clearInterval(interval);
  }, [form, saveDraft, submitted]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleFieldChange = (fieldName, value) => {
    if (submittedRef.current || !fieldName) {
      return;
    }

    setFormData((previousData) => {
      const nextData = {
        ...previousData,
        [fieldName]: value,
      };
      latestDataRef.current = nextData;
      return nextData;
    });
  };

  const handleFieldBlur = () => {
    if (submittedRef.current) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => saveDraft(latestDataRef.current, true), 250);
  };

  const handleSubmit = () => {
    if (!form || submittedRef.current) {
      return;
    }

    const missingFields = getMissingRequiredFields({ sections: form.sections || [] }, latestDataRef.current);

    if (missingFields.length > 0) {
      alert(`Complete the required fields before submitting this form:\n- ${missingFields.map((field) => field.label).join('\n- ')}`);
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    submittedRef.current = true;
    setSaving(true);
    setError(null);

    submitPublicDynamicFormsForm(token, latestDataRef.current, responseUidRef.current)
      .then((updatedForm) => {
        setForm(updatedForm);
        setSubmitted(true);
        setSaving(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((submitError) => {
        submittedRef.current = false;
        console.error('Failed to submit public dynamic form:', submitError);
        setError('Unable to submit this form. The link may have expired or the form may already be complete.');
        setSaving(false);
      });
  };

  const handleAddAnotherResponse = () => {
    if (!form?.allowMultiplePublicSubmissions) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    setLoading(true);
    setError(null);
    setSubmitted(false);
    submittedRef.current = false;
    responseUidRef.current = null;

    loadPublicDynamicFormsForm(token)
      .then((loadedForm) => {
        const data = loadedForm.data || {};
        responseUidRef.current = loadedForm.responseUid || null;
        setForm(loadedForm);
        setFormData(data);
        latestDataRef.current = data;
        setLastSavedAt(null);
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((loadError) => {
        console.error('Failed to start another public dynamic form response:', loadError);
        setError(loadError.status === 404 ? 'This form link is invalid, expired, or no longer available.' : 'Unable to start another response right now.');
        setLoading(false);
      });
  };

  if (loading) {
    return <div className="dynamicforms-page df-public-page"><div className="df-public-card">Loading form...</div></div>;
  }

  if (error && !form) {
    return <div className="dynamicforms-page df-public-page"><div className="df-public-card"><h2>Form unavailable</h2><p>{error}</p></div></div>;
  }

  const draftStatusText = lastSavedAt
    ? `Draft saved at ${formatDraftSavedTime(lastSavedAt)}`
    : 'Drafts save automatically.';

  return (
    <div className="dynamicforms-page df-public-page">
      <div className="df-public-card">
        <div className="df-public-header">
          <p className="eyebrow">Smart PCMH Dynamic Forms</p>
          <h1>{form.title || form.templateName || 'Shared form'}</h1>
          {form.templateDescription && <p>{form.templateDescription}</p>}
        </div>

        {submitted && (
          <div className="alert alert-success df-public-thank-you">
            <p>Thank you. Your response has been submitted.</p>
            {form.allowMultiplePublicSubmissions && (
              <button className="btn btn-primary" type="button" onClick={handleAddAnotherResponse}>
                Add another response
              </button>
            )}
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <div className="df-form-sections">
          {(form.sections || []).map((section) => (
            <section key={section.id || section.title} className="df-form-section">
              <div className="df-form-section-header">
                <div>
                  <h2 className="df-form-section-title">{section.title || 'Section'}</h2>
                  {section.description && <p className="df-form-section-subtitle">{section.description}</p>}
                </div>
              </div>
              <div className="df-form-grid">
                {(section.fields || []).map((field) => {
                  const baseFieldName = field.name || `field_${field.id}`;
                  const sourceFieldName = getMetadataFieldName(baseFieldName, 'source');
                  const noteFieldName = getMetadataFieldName(baseFieldName, 'note');
                  const sourceValue = formData[sourceFieldName] ?? '';
                  const noteValue = formData[noteFieldName] ?? '';

                  return (
                    <article key={field.id || field.name} className="df-form-field df-form-field--span-12">
                      <FormField
                        field={field}
                        value={isAnswerableField(field) ? formData[field.name] : undefined}
                        onChange={handleFieldChange}
                        onBlur={handleFieldBlur}
                        disabled={submitted}
                      />
                      <details className="df-metadata-section df-field-metadata">
                        <summary>Source (interview reference) and Note</summary>
                        <div className="field-metadata">
                          <div className="form-group" style={{ marginTop: '12px', marginBottom: '12px' }}>
                            <label className="form-label">Source (interview reference)</label>
                            <input
                              type="text"
                              className="form-input"
                              value={sourceValue}
                              onChange={(event) => handleFieldChange(sourceFieldName, event.target.value)}
                              onBlur={handleFieldBlur}
                              readOnly={submitted}
                            />
                          </div>
                          <div className="form-group" style={{ marginTop: '12px' }}>
                            <label className="form-label">Note</label>
                            <textarea
                              className="form-textarea"
                              rows={3}
                              value={noteValue}
                              onChange={(event) => handleFieldChange(noteFieldName, event.target.value)}
                              onBlur={handleFieldBlur}
                              readOnly={submitted}
                            />
                          </div>
                        </div>
                      </details>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {!submitted && (
          <div className="df-public-actions">
            <span>{saving ? 'Saving...' : draftStatusText}</span>
            <button className="btn btn-primary" type="button" onClick={handleSubmit} disabled={saving}>
              Submit form
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicFormPage;
